/**
 * Utility functions for backtest performance calculations
 */

/**
 * Calculate total return percentage
 * @param {number} initialCapital - Starting capital
 * @param {number} finalValue - Ending portfolio value
 * @returns {number} Total return percentage (as decimal)
 */
export const calculateTotalReturn = (initialCapital, finalValue) => {
  if (!initialCapital || initialCapital === 0) {
    return 0;
  }
  return (finalValue - initialCapital) / initialCapital;
};

/**
 * Calculate win rate from trades
 * @param {Array} trades - Array of trade objects
 * @returns {number} Win rate (as decimal)
 */
export const calculateWinRate = (trades) => {
  if (!trades || trades.length === 0) {
    return 0;
  }
  
  const winningTrades = trades.filter(trade => trade.pnl > 0);
  return winningTrades.length / trades.length;
};

/**
 * Calculate maximum drawdown
 * @param {Array} equity - Array of equity curve points
 * @returns {Object} { maxDrawdown, maxDrawdownAmount, peakValue }
 */
export const calculateMaxDrawdown = (equity) => {
  if (!equity || equity.length === 0) {
    return { maxDrawdown: 0, maxDrawdownAmount: 0, peakValue: 0 };
  }

  let peak = equity[0].value;
  let maxDrawdown = 0;
  let maxDrawdownAmount = 0;

  equity.forEach(point => {
    if (point.value > peak) {
      peak = point.value;
    }
    
    const drawdown = (peak - point.value) / peak;
    const drawdownAmount = peak - point.value;
    
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
      maxDrawdownAmount = drawdownAmount;
    }
  });

  return { maxDrawdown, maxDrawdownAmount, peakValue: peak };
};

/**
 * Calculate Sharpe ratio
 * @param {Array} returns - Array of daily returns
 * @param {number} riskFreeRate - Risk-free rate (default 0.02 for 2%)
 * @returns {number} Sharpe ratio
 */
export const calculateSharpeRatio = (returns, riskFreeRate = 0.02) => {
  if (!returns || returns.length === 0) {
    return 0;
  }

  const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - avgReturn, 2), 0) / returns.length;
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) {
    return 0;
  }
  
  return (avgReturn - riskFreeRate) / stdDev;
};

/**
 * Calculate profit factor
 * @param {Array} trades - Array of trade objects
 * @returns {number} Profit factor
 */
export const calculateProfitFactor = (trades) => {
  if (!trades || trades.length === 0) {
    return 0;
  }

  const grossProfit = trades
    .filter(trade => trade.pnl > 0)
    .reduce((sum, trade) => sum + trade.pnl, 0);
    
  const grossLoss = Math.abs(trades
    .filter(trade => trade.pnl < 0)
    .reduce((sum, trade) => sum + trade.pnl, 0));

  return grossLoss === 0 ? grossProfit : grossProfit / grossLoss;
};

/**
 * Calculate average trade metrics
 * @param {Array} trades - Array of trade objects
 * @returns {Object} { avgWin, avgLoss, avgTrade }
 */
export const calculateAverageTradeMetrics = (trades) => {
  if (!trades || trades.length === 0) {
    return { avgWin: 0, avgLoss: 0, avgTrade: 0 };
  }

  const winningTrades = trades.filter(trade => trade.pnl > 0);
  const losingTrades = trades.filter(trade => trade.pnl < 0);

  const avgWin = winningTrades.length > 0 
    ? winningTrades.reduce((sum, trade) => sum + trade.pnl, 0) / winningTrades.length 
    : 0;

  const avgLoss = losingTrades.length > 0 
    ? Math.abs(losingTrades.reduce((sum, trade) => sum + trade.pnl, 0) / losingTrades.length)
    : 0;

  const avgTrade = trades.reduce((sum, trade) => sum + trade.pnl, 0) / trades.length;

  return { avgWin, avgLoss, avgTrade };
};

/**
 * Calculate trade duration statistics
 * @param {Array} trades - Array of trade objects
 * @returns {Object} { avgDuration, minDuration, maxDuration }
 */
export const calculateTradeDurationStats = (trades) => {
  if (!trades || trades.length === 0) {
    return { avgDuration: 0, minDuration: 0, maxDuration: 0 };
  }

  const durations = trades.map(trade => trade.duration || 0);
  const avgDuration = durations.reduce((sum, duration) => sum + duration, 0) / durations.length;
  const minDuration = Math.min(...durations);
  const maxDuration = Math.max(...durations);

  return { avgDuration, minDuration, maxDuration };
};

/**
 * Calculate comprehensive performance metrics
 * @param {Object} backtestData - Complete backtest data
 * @returns {Object} All calculated metrics
 */
export const calculateAllMetrics = (backtestData) => {
  if (!backtestData || !backtestData.results) {
    return null;
  }

  const { results } = backtestData;
  const { trades = [], equity = [] } = results;

  // Calculate returns from equity curve
  const returns = [];
  for (let i = 1; i < equity.length; i++) {
    const prevValue = equity[i-1].value;
    // Prevent division by zero
    if (prevValue === 0) {
      returns.push(0);
    } else {
      const dailyReturn = (equity[i].value - prevValue) / prevValue;
      returns.push(dailyReturn);
    }
  }

  const { maxDrawdown, maxDrawdownAmount } = calculateMaxDrawdown(equity);
  const { avgWin, avgLoss, avgTrade } = calculateAverageTradeMetrics(trades);
  const { avgDuration } = calculateTradeDurationStats(trades);

  return {
    totalReturn: equity.length > 0 ? equity[equity.length - 1].value - equity[0].value : 0,
    totalReturnPercent: equity.length > 0 ? calculateTotalReturn(equity[0].value, equity[equity.length - 1].value) : 0,
    winRate: calculateWinRate(trades),
    totalTrades: trades.length,
    winCount: trades.filter(trade => trade.pnl > 0).length,
    lossCount: trades.filter(trade => trade.pnl < 0).length,
    maxDrawdown,
    maxDrawdownAmount,
    sharpeRatio: calculateSharpeRatio(returns),
    avgWin,
    avgLoss,
    avgTrade,
    profitFactor: calculateProfitFactor(trades),
    avgDuration
  };
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format percentage for display
 * @param {number} value - Decimal value (e.g., 0.15 for 15%)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value) => {
  return `${(value * 100).toFixed(2)}%`;
};

/**
 * Format number with appropriate precision
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  return Number(value).toFixed(decimals);
};

/**
 * Resolve the equity curve a single backtest result should display.
 *
 * In portfolio mode the shared-capital curve is stored on `portfolioEquityCurve`
 * and the per-symbol `equityCurve` is left empty. The multi-symbol "All" view
 * already swaps in the portfolio curve, but single-result / single-symbol views
 * return the raw result — so without this they render no equity curve (and hence
 * no drawdown chart, which is derived from it). When the per-symbol curve is
 * empty and a portfolio curve exists, fall back to the portfolio curve.
 *
 * @param {object} result - a single backtest result row
 * @returns {object} the result with `equityCurve` populated for charting
 */
export const withDisplayEquityCurve = (result) => {
  if (!result) {
    return result;
  }
  const perSymbol = Array.isArray(result.equityCurve) ? result.equityCurve : [];
  if (perSymbol.length > 0) {
    return result;
  }
  const portfolioCurve = Array.isArray(result.portfolioEquityCurve)
    ? result.portfolioEquityCurve
    : [];
  if (portfolioCurve.length === 0) {
    return result;
  }
  return {
    ...result,
    equityCurve: portfolioCurve.map((point) => ({
      date: point.date,
      equity: point.equity,
      cash: point.cash,
      openPositions: point.openPositions,
      deployedCapital: point.deployedCapital,
      symbol: 'Portfolio',
    })),
  };
};